import { ITextStyle, TextStyle } from '@pixi/text';
import { BitmapFont, IBitmapTextStyle, TextMetrics } from 'pixi.js';

import { getTextStyles, TextProps } from '../../../node/text';

export type WebGLStyle = Partial<ITextStyle> | ITextStyle;
export type BitmapStyle = Partial<IBitmapTextStyle> | IBitmapTextStyle;
export type Style = WebGLStyle | BitmapStyle | undefined;
export type Token = string;
export type Size = { width: number; height: number };

export type TextSize = {
  width: number;
  height: number;
};

export const tokenCacheMap = new Map<Style, Record<Token, Size>>();

export const _measureWebGLText = (
  text: string,
  style: WebGLStyle,
): TextSize => {
  const textStyle = new TextStyle(style);

  return TextMetrics.measureText(text, textStyle);
};

export const _measureBitmapText = (
  text: string,
  style: BitmapStyle,
): TextSize => {
  const data = BitmapFont.available[style.fontName || 'OpenSans-Regular'];
  const scale = (style.fontSize || 0) / data.size;
  const textByChar = text.split('');

  const width = textByChar.reduce<number>((acc, char) => {
    const bitmapCharData = data.chars[char.codePointAt(0) || 0];

    return acc + (bitmapCharData?.xAdvance || 0) * scale;
  }, 0);

  return {
    width,
    height: data.lineHeight * scale,
  };
};

export const _measureText = (text: string, style: Style): TextSize => {
  return (style as IBitmapTextStyle | undefined)?.tint
    ? _measureBitmapText(text, style as BitmapStyle)
    : _measureWebGLText(text, style as WebGLStyle);
};

export const _getMeasureSize = (measure: TextSize): Size => {
  const width = measure.width;
  const height = measure.height;

  return { width, height };
};

export const getCachedTokenSize = (token: Token, props: TextProps): Size => {
  const style = getTextStyles(props);
  const savedStyleKey = tokenCacheMap.get(style);

  if (!savedStyleKey) {
    const measure = _measureText(token, style);
    const size = _getMeasureSize(measure);

    tokenCacheMap.set(style, { [token]: size });

    return size;
  }

  if (savedStyleKey[token] !== undefined) {
    return savedStyleKey[token];
  }

  const measure = _measureText(token, style);
  const size = _getMeasureSize(measure);

  savedStyleKey[token] = size;

  return size;
};
